import { Button, Headings, Paragraph } from '@sentara-health/optimahealth_web';
import {
  HeaderLogo,
  ContactUsLink,
  HamburgerMenuLink,
  FooterNav,
  FooterDisclaimer,
} from 'organisms';
import { useNavigate } from 'react-router-dom';
import { NavigateConst, domainForSegment, ANALYTICS_MESSAGES } from 'common';
import { isAvMed } from 'context';
import { useTranslation } from 'react-i18next';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { useContext } from 'react';
import { GlobalContext } from 'context';

export const DrAuthorizationChooser = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { contentListingItemClicked } = useAdobeAnalytics();
  const { getFeature } = useContext(GlobalContext);

  // Return null if DR feature is not enabled
  if (getFeature && !getFeature.DesignatedRepresentativeTab) {
    return null;
  }

  const trackClick = (
    config: (typeof ANALYTICS_MESSAGES.DR_CONTENT_LISTING)[keyof typeof ANALYTICS_MESSAGES.DR_CONTENT_LISTING]
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}${window.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [{ ...config }],
        },
      },
    });
  };

  const cards = [
    {
      key: 'signIn',
      title: t('profile.designatedRepresentative.haveUsernameTitle'),
      description: t('profile.designatedRepresentative.haveUsernameDescription'),
      button: t('common.signIn'),
      onClick: () => {
        trackClick(ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_SIGNIN_CLICK);
        navigate(NavigateConst.signIn);
      },
    },
    {
      key: 'register',
      title: t('profile.designatedRepresentative.noUsernameTitle'),
      description: t('profile.designatedRepresentative.noUsernameDescription'),
      button: t('common.registerButton'),
      onClick: () => {
        trackClick(ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_REGISTER_NOW_CLICK);
        navigate(NavigateConst.drRegistration);
      },
    },
  ];

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <header className="border-bottom">
        <div className="container-fluid">
          <nav className="header container-xl d-flex align-items-center justify-content-between">
            <HeaderLogo />

            <div className="d-none d-lg-block">
              <ContactUsLink showIcon={false} />
            </div>

            <div className="d-lg-none">
              <HamburgerMenuLink showLogout={false} />
            </div>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="d-flex align-items-center py-5 px-3 row justify-content-center">
            <section className="col-12 col-md-10 col-lg-8 text-center">
              <Headings
                level={1}
                text={t('common.welcomeTitle')}
                variant="medium"
                className="mb-4"
              />

              <Paragraph
                text={t('profile.designatedRepresentative.completeAuthMessage')}
                variant="bold"
                className="mb-2"
                level={3}
              />

              <Paragraph
                text={t('profile.designatedRepresentative.linkExpiryMessage')}
                variant="regular"
                className="mb-5"
                level={3}
              />

              <div className="row g-4 mt-4">
                {cards.map(({ key, title, description, button, onClick }) => (
                  <div key={key} className="col-12 col-md-6 d-flex">
                    <section className="border rounded p-4 w-100 text-center">
                      <div>
                        <Paragraph
                          level={4}
                          text={title}
                          variant="bold"
                          textColor="secondary"
                          className="mb-3"
                        />

                        <Paragraph
                          text={description}
                          variant="regular"
                          className="mb-4"
                          level={3}
                        />
                      </div>

                      <Button
                        variant="primary"
                        onClick={onClick}
                        className="px-4 text-nowrap"
                      >
                        {button}
                      </Button>
                    </section>
                  </div>
                ))}
              </div>
            </section>
         
      </main>

      {/* Footer */}
      <footer className={isAvMed() ? '' : 'mt-auto'}>
        {isAvMed() && <FooterNav />}

        <div className="container-xl">
          <FooterDisclaimer />
        </div>
      </footer>
    </div>
  );
};